import { Box, GlobalStyles } from "@mui/material";
import { Clear, RotateClockwise, RotateCounterClockwise } from "@procore/core-icons";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { EventOrigin, Events, track } from "apis/procore";
import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import type { ToolbarButtonEntry } from "contexts/toolbars";
import { toolbarsActions, useDispatchToolbars } from "contexts/toolbars";
import { coordinatesActions } from "features/coordinates";
import { useResetView } from "features/home/useResetView";
import { measureActions } from "features/measure/measureSlice";
import { clippingOutlineLaserActions } from "features/outlineLaser/outlineLaserSlice";
import { pointLineActions } from "features/pointLine/pointLineSlice";
import { renderActions } from "features/render";
import {
    selectCanClear,
    selectIsRedoEmpty,
    selectIsUndoEmpty,
    selectPeekRedo,
    selectPeekUndo,
    undoRedoActions,
} from "features/undoRedo";
import {
    useModificationPill,
    useViewModificationCategories,
    ViewModificationsFlyout,
} from "features/viewModifications";
import ResetIcon from "media/icons/reset.svg?react";
import { ProcoreToolMode, selectActiveToolMode } from "slices/procoreUx";
import { isMacintosh } from "utils/misc";

import { QUICK_ACCESS_TOOLBAR, TOOLBAR_BUTTON, TOOLBAR_CONTAINER } from "../constants";
import { Toolbar } from "../Toolbar";
import { useFlyoutRegistration } from "../useFlyoutRegistration";
import { BADGE_KEYFRAMES, ModificationDot, ModificationPill } from "./ModificationBadges";

interface UndoRedoToolbarProps {
    id?: string;
}

// Useful for debugging undo/redo stack w/out reloading
const SHOW_CLEAR_BUTTON = false;

// Pill anchor position relative to the toolbar container.
// Aligns the pill so its right edge overlaps the reset button's top-left corner,
// matching the dot position — the pill visually collapses into the dot.
const PILL_RIGHT = TOOLBAR_CONTAINER.WIDTH - TOOLBAR_CONTAINER.PADDING - 8;
const PILL_TOP = TOOLBAR_CONTAINER.PADDING + TOOLBAR_BUTTON.SIZE / 2;

/**
 * UndoRedo toolbar: Reset (with View Modifications flyout), Undo, Redo.
 *
 * Reset button behavior:
 *   - Default mode: toggles the View Modifications flyout
 *   - Active mode (Measure/Section): clears that mode's work, stays in the mode
 */
export function UndoRedoToolbar({ id = "undo-redo-toolbar" }: UndoRedoToolbarProps) {
    const toolbarDispatch = useDispatchToolbars();
    const dispatch = useAppDispatch();

    const [isMenuOpen, setIsMenuOpen] = useState(false);
    useFlyoutRegistration(isMenuOpen);
    const resetBtnRef = useRef<HTMLButtonElement>(null);
    const flyoutRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [flyoutTop, setFlyoutTop] = useState(0);

    useEffect(() => {
        if (isMenuOpen && resetBtnRef.current && containerRef.current) {
            const btnTop = resetBtnRef.current.getBoundingClientRect().top;
            const containerTop = containerRef.current.getBoundingClientRect().top;
            setFlyoutTop(btnTop - containerTop);
        }
    }, [isMenuOpen]);

    // Close flyout on click outside (exclude the reset button so toggle still works)
    useEffect(() => {
        if (!isMenuOpen) {
            return;
        }
        const handleMouseDown = (e: MouseEvent) => {
            const btn = resetBtnRef.current;
            const flyout = flyoutRef.current;
            if (btn?.contains(e.target as Node) || flyout?.contains(e.target as Node)) {
                return;
            }
            setIsMenuOpen(false);
        };
        document.addEventListener("mousedown", handleMouseDown);
        return () => document.removeEventListener("mousedown", handleMouseDown);
    }, [isMenuOpen]);

    const nextUndo = useAppSelector(selectPeekUndo);
    const nextRedo = useAppSelector(selectPeekRedo);
    const isUndoEmpty = useAppSelector(selectIsUndoEmpty);
    const isRedoEmpty = useAppSelector(selectIsRedoEmpty);
    const canClear = useAppSelector(selectCanClear);
    const activeToolMode = useAppSelector(selectActiveToolMode);

    const resetView = useResetView();
    const categories = useViewModificationCategories();
    const { pillPhase, dotVisible, dotExiting, displayLabel } = useModificationPill(isMenuOpen);

    const isDefaultMode = activeToolMode === ProcoreToolMode.Default;

    // Close the flyout when entering an active tool mode
    useEffect(() => {
        if (!isDefaultMode) {
            setIsMenuOpen(false);
        }
    }, [isDefaultMode]);

    // Close the flyout when all modifications are cleared
    useEffect(() => {
        if (categories.length === 0) {
            setIsMenuOpen(false);
        }
    }, [categories.length]);

    const handleResetClick = useCallback(() => {
        if (activeToolMode === ProcoreToolMode.Measure) {
            dispatch(coordinatesActions.clear());
            dispatch(clippingOutlineLaserActions.clear());
            dispatch(pointLineActions.clear());
            dispatch(measureActions.clear());
            return;
        }
        if (activeToolMode === ProcoreToolMode.Section) {
            dispatch(renderActions.setClippingPlanes({ planes: [], enabled: false }));
            return;
        }
        // Default mode: toggle the flyout
        setIsMenuOpen((open) => !open);
        void track(Events.toolbar.resetTapped, { origin: EventOrigin.Toolbar });
    }, [activeToolMode, dispatch]);

    const handleResetAll = useCallback(async () => {
        setIsMenuOpen(false);
        await resetView({
            resetCamera: false,
            resetVersionComparisonPropertyTree: false,
            preservePicker: true,
        });
        void track(Events.toolbar.resetTapped, { origin: EventOrigin.Toolbar });
    }, [resetView]);

    const buttons = useMemo(() => {
        const mainButtons: ToolbarButtonEntry[] = [
            {
                config: {
                    id: "reset",
                    icon: <ResetIcon />,
                    ref: resetBtnRef,
                    onClick: handleResetClick,
                    hasExpander: isDefaultMode,
                    expanderPosition: "bottom-left",
                    tooltip: {
                        toolName: isDefaultMode ? "reset" : "resetMode",
                        label: "Reset",
                        alwaysVisible: false,
                        placement: "left",
                        shortcutKey: isDefaultMode ? (isMacintosh ? "Cmd+R" : "Ctrl+R") : "",
                    },
                },
                state: {
                    active: isMenuOpen,
                    disabled: false,
                    visible: true,
                },
            },
            {
                config: {
                    id: "undo",
                    icon: <RotateCounterClockwise />,
                    onClick: () => {
                        if (nextUndo === undefined) {
                            return;
                        }
                        nextUndo.undoCallback();
                        dispatch(undoRedoActions.undo());
                        void track(Events.toolbar.undoTapped, { origin: EventOrigin.Toolbar });
                    },
                    tooltip: {
                        toolName: "undo",
                        shortcutKey: isMacintosh ? "Cmd+Z" : "Ctrl+Z",
                        alwaysVisible: false,
                        placement: "left",
                    },
                },
                state: {
                    active: false,
                    disabled: isUndoEmpty,
                    visible: true,
                },
            },
            {
                config: {
                    id: "redo",
                    icon: <RotateClockwise />,
                    onClick: () => {
                        if (nextRedo === undefined) {
                            return;
                        }
                        nextRedo.redoCallback();
                        dispatch(undoRedoActions.redo());
                        void track(Events.toolbar.redoTapped, { origin: EventOrigin.Toolbar });
                    },
                    tooltip: {
                        toolName: "redo",
                        shortcutKey: isMacintosh ? "Cmd+Shift+Z / Cmd+Y" : "Ctrl+Y",
                        alwaysVisible: false,
                        placement: "left",
                    },
                },
                state: {
                    active: false,
                    disabled: isRedoEmpty,
                    visible: true,
                },
            },
        ];

        if (SHOW_CLEAR_BUTTON) {
            mainButtons.push({
                config: {
                    id: "clear",
                    icon: <Clear />,
                    onClick: () => {
                        dispatch(undoRedoActions.clear());
                    },
                    title: "Clear",
                    tooltip: {
                        toolName: "clear",
                        shortcutKey: "",
                        alwaysVisible: false,
                        placement: "left",
                    },
                },
                state: {
                    active: false,
                    disabled: canClear,
                    visible: true,
                },
            });
        }

        return mainButtons;
    }, [handleResetClick, isDefaultMode, isMenuOpen, isUndoEmpty, isRedoEmpty, canClear, dispatch, nextUndo, nextRedo]);

    useEffect(() => {
        toolbarDispatch(toolbarsActions.addToolbar({ id, buttons }));
        return () => {
            toolbarDispatch(toolbarsActions.removeToolbar(id));
        };
    }, [toolbarDispatch, id, buttons]);

    return (
        <>
            <GlobalStyles styles={BADGE_KEYFRAMES} />
            {/* fit-content constrains the positioning context to the toolbar width */}
            <Box ref={containerRef} sx={{ position: "relative", width: "fit-content" }}>
                <Toolbar id={id} />

                {pillPhase !== "hidden" && !isMenuOpen && isDefaultMode && (
                    <Box
                        sx={{
                            pointerEvents: "none",
                            position: "absolute",
                            right: PILL_RIGHT,
                            top: PILL_TOP,
                            transform: "translateY(-135%) translateX(15%)",
                            zIndex: 1501, // above MUI tooltip (1500)
                        }}
                    >
                        <ModificationPill label={displayLabel} phase={pillPhase} />
                    </Box>
                )}

                {dotVisible && isDefaultMode && (
                    <Box
                        sx={{
                            pointerEvents: "none",
                            position: "absolute",
                            top: TOOLBAR_CONTAINER.PADDING + 3,
                            left: TOOLBAR_CONTAINER.PADDING + 3,
                            zIndex: 1501, // above MUI tooltip (1500)
                        }}
                    >
                        <ModificationDot exiting={dotExiting} />
                    </Box>
                )}
                {isMenuOpen && (
                    <Box
                        sx={{
                            position: "absolute",
                            top: flyoutTop,
                            right: "100%",
                            zIndex: QUICK_ACCESS_TOOLBAR.FLYOUT_Z_INDEX,
                        }}
                    >
                        <div ref={flyoutRef}>
                            <ViewModificationsFlyout categories={categories} onResetAll={handleResetAll} />
                        </div>
                    </Box>
                )}
            </Box>
        </>
    );
}
